import React from 'react';
import { Button, Space, Typography, Card, Spin, message, Tooltip, Alert } from 'antd';
import {
  WalletOutlined,
  DisconnectOutlined,
  ReloadOutlined,
  CopyOutlined,
  CheckCircleFilled,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { WalletBalances } from '../hooks/useMultiWallet';
import { NETWORKS, SupportedNetwork, currencyIcon, currencyLabel, formatAmount } from '../constants/networks';
import TonBalance from './TonBalance';

const { Text } = Typography;

interface MultiNetworkConnectedCardProps {
  activeNetwork: SupportedNetwork;
  isConnected: boolean;
  currentAddress: string | null;
  truncatedAddress: string;
  /**
   * Адрес, привязанный к аккаунту на сервере, когда живого соединения в этой
   * вкладке нет. Так бывает всегда, если кошелёк подключали в его собственном
   * браузере: привязка осталась, соединение — нет. Показывать «не подключено»
   * в этом случае неправильно: деньги с этого адреса аккаунту зачислятся.
   */
  linkedAddress?: string | null;
  /** Открыть выбор кошелька, в чьём встроенном браузере запустить страницу. */
  onOpenWalletApp?: () => void;
  walletBalances: WalletBalances;
  balancesLoading: boolean;
  connecting?: boolean;
  /** Код причины, почему кошелёк сети недоступен в этом браузере (см. useMultiWallet). */
  unavailableReason?: string | null;
  onConnect: () => void;
  onDisconnect: () => void;
  onRefresh: () => void;
}

/** Карточка «Подключённый кошелёк» для выбранной сети (вне Telegram). */
export const MultiNetworkConnectedCard: React.FC<MultiNetworkConnectedCardProps> = ({
  activeNetwork,
  isConnected,
  currentAddress,
  truncatedAddress,
  linkedAddress = null,
  onOpenWalletApp,
  walletBalances = {},
  balancesLoading,
  connecting = false,
  unavailableReason = null,
  onConnect,
  onDisconnect,
  onRefresh,
}) => {
  const { t } = useTranslation();
  const meta = NETWORKS[activeNetwork];
  // Встроенный браузер кошелька есть только на телефоне; на десктопе кошелёк —
  // это расширение, и предлагать «открыть в приложении» бессмысленно.
  const isMobile = typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);
  const canOpenWalletApp =
    Boolean(onOpenWalletApp) && isMobile && activeNetwork !== 'ton' && activeNetwork !== 'solana';

  // TON с живым соединением: балансы показывает тот же блок, что и в мини-аппе,
  // но заголовок с кнопками «обновить» и «отключить» нужен и здесь — иначе
  // отключить кошелёк можно только из меню в шапке, и в карточке значка нет,
  // хотя у всех остальных сетей он на месте.
  if (activeNetwork === 'ton' && isConnected) {
    return (
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Space size={8}>
            <img
              src={meta.icon}
              alt={meta.name}
              style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'contain' }}
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
            <div>
              <Text strong style={{ fontSize: '14px', display: 'block', lineHeight: 1.2 }}>
                {meta.name}
              </Text>
              <Text type="secondary" style={{ fontSize: '11px', lineHeight: 1 }}>
                {meta.badge}
              </Text>
            </div>
          </Space>
          <Tooltip title={t('Disconnect')}>
            <Button
              type="text"
              size="small"
              danger
              icon={<DisconnectOutlined />}
              onClick={onDisconnect}
              data-testid="disconnect-ton"
            />
          </Tooltip>
        </div>
        <TonBalance />
      </Space>
    );
  }

  // Адрес для показа: живое соединение, иначе привязка на сервере.
  const shownAddress = currentAddress || linkedAddress;
  const linkedOnly = !isConnected && Boolean(linkedAddress);
  const shownTruncated =
    currentAddress && truncatedAddress
      ? truncatedAddress
      : shownAddress
      ? `${shownAddress.slice(0, 6)}…${shownAddress.slice(-4)}`
      : '';

  const handleCopy = () => {
    if (shownAddress) {
      navigator.clipboard.writeText(shownAddress);
      message.success(t('Address copied'));
    }
  };

  const hint = (() => {
    switch (unavailableReason) {
      case 'connector_unavailable':
        return t('Wallet connector is unavailable. Reload the page or try another browser.');
      case 'tron_install_extension':
        return t('Install the TronLink extension to connect a TRON wallet.');
      case 'tron_open_in_app':
        // Кошельков с поддержкой TRON несколько, выбор даёт окно «Открыть в
        // приложении кошелька» — называть один TronLink больше нельзя.
        return t('A TRON wallet connects inside the wallet app: open this page there.');
      default:
        return null;
    }
  })();

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={12}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Space size={8}>
          <img
            src={meta.icon}
            alt={meta.name}
            style={{ width: 24, height: 24, borderRadius: '50%', objectFit: 'contain' }}
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none';
            }}
          />
          <div>
            <Text strong style={{ fontSize: '14px', display: 'block', lineHeight: 1.2 }}>
              {meta.name}
            </Text>
            <Text type="secondary" style={{ fontSize: '11px', lineHeight: 1 }}>
              {meta.badge}
            </Text>
          </div>
        </Space>

        {shownAddress ? (
          <Space size={6}>
            <Tooltip title={t('Refresh balances')}>
              <Button
                type="text"
                size="small"
                icon={<ReloadOutlined spin={balancesLoading} />}
                onClick={onRefresh}
                style={{ color: '#64748b' }}
              />
            </Tooltip>
            <Tooltip title={t('Disconnect')}>
              <Button type="text" size="small" danger icon={<DisconnectOutlined />} onClick={onDisconnect} />
            </Tooltip>
          </Space>
        ) : null}
      </div>

      {!shownAddress ? (
        <>
          <Button
            type="primary"
            block
            size="large"
            icon={<WalletOutlined />}
            loading={connecting}
            onClick={onConnect}
            data-testid={`connect-${activeNetwork}`}
            style={{
              height: '46px',
              borderRadius: '12px',
              fontWeight: 600,
              background: meta.color,
              borderColor: meta.color,
            }}
          >
            {t('Connect {{network}} wallet', { network: meta.name })}
          </Button>
          {canOpenWalletApp && (
            <Button type="link" block onClick={onOpenWalletApp} style={{ fontSize: 12, height: 'auto', padding: 0 }}>
              {t('Open in a wallet app')}
            </Button>
          )}
          {hint && <Alert type="warning" showIcon message={hint} style={{ borderRadius: 10, fontSize: 12 }} />}
        </>
      ) : (
        <Space direction="vertical" style={{ width: '100%' }} size={8}>
          <div
            onClick={handleCopy}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '6px 12px',
              background: '#f8fafc',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            <Space size={6}>
              <CheckCircleFilled style={{ color: linkedOnly ? '#f59e0b' : '#52c41a', fontSize: '12px' }} />
              <Text style={{ fontSize: '12px', fontFamily: 'ui-monospace, monospace', color: '#334155' }}>
                {shownTruncated}
              </Text>
            </Space>
            <CopyOutlined style={{ color: '#94a3b8', fontSize: '13px' }} />
          </div>

          {linkedOnly && (
            <>
              <Text type="secondary" style={{ fontSize: 11, lineHeight: 1.4, display: 'block' }}>
                {t('Wallet linked to your account, but not connected in this browser.')}
              </Text>
              <Button
                block
                icon={<WalletOutlined />}
                loading={connecting}
                onClick={onConnect}
                data-testid={`connect-${activeNetwork}`}
                style={{ height: '40px', borderRadius: '10px', fontWeight: 600 }}
              >
                {t('Connect {{network}} wallet', { network: meta.name })}
              </Button>
              {hint && <Alert type="warning" showIcon message={hint} style={{ borderRadius: 10, fontSize: 12 }} />}
            </>
          )}

          {/* Балансы TON приходят из TonBalance (он показывается при живом
              соединении). Для привязанного без соединения TON-адреса своего
              источника балансов нет — вместо нулей не показываем ничего. */}
          <div
            style={{
              display: activeNetwork === 'ton' ? 'none' : 'flex',
              flexDirection: 'column',
              gap: '8px',
              marginTop: '4px',
            }}
          >
            {meta.tokens.map((symbol) => {
              const val = walletBalances[symbol];
              return (
                <Card
                  key={symbol}
                  size="small"
                  style={{
                    background: '#f8fafc',
                    borderRadius: '10px',
                    border: '1px solid #edf2f7',
                  }}
                  bodyStyle={{ padding: '8px 12px' }}
                >
                  <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
                    <Space size={8}>
                      <img
                        src={currencyIcon(symbol)}
                        alt={symbol}
                        width={16}
                        height={16}
                        style={{ objectFit: 'contain', verticalAlign: 'middle' }}
                        onError={(e) => {
                          (e.currentTarget as HTMLImageElement).style.display = 'none';
                        }}
                      />
                      <Text style={{ fontSize: '13px', fontWeight: 500 }}>
                        {currencyLabel(symbol)}
                      </Text>
                    </Space>
                    <Text strong style={{ fontSize: '13px' }}>
                      {balancesLoading ? <Spin size="small" /> : formatAmount(symbol, val ?? 0)}
                    </Text>
                  </Space>
                </Card>
              );
            })}
          </div>
        </Space>
      )}
    </Space>
  );
};

export default MultiNetworkConnectedCard;
